/**
 * unknown-provider-queue-page.tsx — Queue list page for unknown provider referrals.
 *
 * Combines useReferralQueue hook with ReferralQueueFilters + ReferralQueueTable.
 * Status filter drives server refetch; name/NPI search is client-side via ReferralQueueFilters.
 */

import { useState, type JSX } from 'react';
import { useLocation } from 'react-router';

import type { PendingReferralSummary, ReferralQueueStatus } from './referral-api.js';
import { ReferralQueueFilters } from './referral-queue-filters.js';
import { ReferralQueueTable } from './referral-queue-table.js';
import { useReferralQueue } from './use-referral-queue.js';

export function UnknownProviderQueuePage(): JSX.Element {
  const [status, setStatus] = useState<ReferralQueueStatus>('awaiting_provider_review');
  const { items, isLoading, error, refetch } = useReferralQueue(status);
  const [filteredItems, setFilteredItems] = useState<PendingReferralSummary[]>([]);
  const location = useLocation();
  const successMessage =
    (location.state as { successMessage?: string } | null)?.successMessage ?? null;

  return (
    <div className="mx-auto max-w-6xl space-y-4 p-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-gray-900">Unknown Provider Review Queue</h1>
        <button
          type="button"
          onClick={refetch}
          className="rounded border border-gray-300 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-50"
        >
          Refresh
        </button>
      </div>

      {successMessage && (
        <div
          role="status"
          className="rounded bg-green-50 px-4 py-3 text-sm font-medium text-green-800"
        >
          {successMessage}
        </div>
      )}

      <ReferralQueueFilters items={items} onFilter={setFilteredItems} onStatusChange={setStatus} />

      {error && <p className="rounded bg-red-50 px-3 py-2 text-sm text-red-700">{error.message}</p>}

      <ReferralQueueTable items={filteredItems} isLoading={isLoading} activeStatus={status} />
    </div>
  );
}
